import { supabase } from './supabase';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://backend.heads-up-poker.ru';

// Best-effort — never throw, never block user flow
async function rpc(fn: string, params: Record<string, any>) {
  try {
    const { error } = await supabase.rpc(fn, params);
    if (error) console.error(`[notify] ${fn} error:`, error.message);
    else console.log(`[notify] ${fn} sent OK`);
  } catch (e) {
    console.error(`[notify] ${fn} exception:`, e);
  }
}

// Send notification via backend (triggers both TG bot + Web Push)
async function backendNotify(type: string, userId: number, data: Record<string, any>) {
  try {
    fetch(`${BACKEND_URL}/api/notifications/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, userId, data }),
    }).catch(() => {});
  } catch {}
}

async function backendBroadcast(type: string, data: Record<string, any>) {
  try {
    fetch(`${BACKEND_URL}/api/notifications/broadcast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, data }),
    }).catch(() => {});
  } catch {}
}

// ═══ Direct message to user ═══

export function notifyUser(userId: number, text: string) {
  return rpc('notify_user', { p_user_id: userId, p_text: text });
}

// ═══ Broadcast to all users ═══

export function notifyAll(text: string) {
  return rpc('notify_all_users', { p_text: text });
}

// ═══ Registration ═══

export function notifyRegistrationConfirmed(userId: number, tournamentName: string, date: string) {
  backendNotify('registration_confirmed', userId, { tournamentName, date });
  return notifyUser(userId,
    `✅ <b>Вы записаны на турнир!</b>\n\n🏆 ${tournamentName}\n📅 ${date}\n\nНе забудьте прийти. Если не сможете — отмените запись заранее.`
  );
}

export function notifyWaitlisted(userId: number, tournamentName: string, position: number) {
  backendNotify('waitlisted', userId, { tournamentName, position });
  return notifyUser(userId,
    `⏳ <b>Вы в листе ожидания</b>\n\n🏆 ${tournamentName}\n📍 Позиция: #${position}\n\nМы уведомим вас, если освободится место.`
  );
}

export function notifyPromotedFromWaitlist(userId: number, tournamentName: string) {
  backendNotify('promoted_from_waitlist', userId, { tournamentName });
  return notifyUser(userId,
    `🎉 <b>Место освободилось!</b>\n\n🏆 ${tournamentName}\n\nВы перемещены из листа ожидания в участники!`
  );
}

export function notifyRegistrationCancelled(userId: number, tournamentName: string) {
  backendNotify('registration_cancelled', userId, { tournamentName });
  return notifyUser(userId,
    `❌ <b>Запись отменена</b>\n\n🏆 ${tournamentName}`
  );
}

// ═══ Broadcast ═══

export function notifyRegistrationOpen(tournamentName: string, date: string, maxPlayers: number) {
  backendBroadcast('registration_open', { tournamentName, date, spotsLeft: maxPlayers });
  return notifyAll(
    `📢 <b>Регистрация открыта!</b>\n\n🏆 ${tournamentName}\n📅 ${date}\n👥 Мест: ${maxPlayers}\n\nЗаписывайтесь!`
  );
}

export function notifySpotsAlmostFull(tournamentName: string, spotsLeft: number) {
  backendBroadcast('spots_almost_full', { tournamentName, spotsLeft });
  return notifyAll(
    `🔥 <b>Осталось ${spotsLeft} мест!</b>\n\n🏆 ${tournamentName}\n\nУспейте записаться!`
  );
}

// ═══ Results ═══

export function notifyResults(userId: number, tournamentName: string, position: number, pointsEarned: number, coinsEarned: number) {
  backendNotify('results_published', userId, { tournamentName, position, pointsEarned, coinsEarned });
  return notifyUser(userId,
    `🏆 <b>Результаты турнира</b>\n\n🎯 ${tournamentName}\n📍 Место: #${position}\n⭐ Рейтинг: +${pointsEarned}\n💰 Монеты: +${coinsEarned}`
  );
}

export function notifyFreeEntries(userId: number, count: number) {
  backendNotify('free_entries_awarded', userId, { count });
  return notifyUser(userId,
    `🎟 <b>Вам начислены карточки!</b>\n\n+${count} ${count === 1 ? 'карточка' : 'карточки'} бесплатного входа`
  );
}

export function notifyLevelUp(userId: number, newLevel: number, levelName: string) {
  backendNotify('level_up', userId, { newLevel, levelName });
  return notifyUser(userId,
    `🆙 <b>Новый уровень!</b>\n\n🎮 Уровень ${newLevel} — ${levelName}\n\nПоздравляем!`
  );
}

// ═══ Orders ═══

export function notifyOrderReady(userId: number, orderItems: string) {
  backendNotify('order_ready', userId, { orderItems });
  return notifyUser(userId,
    `🍽 <b>Ваш заказ готов!</b>\n\n${orderItems}`
  );
}
